const CLAIM_STORAGE_PREFIX = "create.pendingSubmittedMessage.claim.v1.";
const CLAIM_COMPLETED_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Safari has no Web Locks API, and background-tab timer throttling can delay a
// heartbeat substantially. Keep the lease conservative so a live send is not
// mistaken for an orphan; lock-aware browsers still reclaim immediately.
const CLAIM_IN_FLIGHT_LEASE_MS = 2 * 60 * 1000;
const CLAIM_HEARTBEAT_MS = 20 * 1000;
const CLAIM_POLL_MAX_MS = 1000;
const CLAIM_DATABASE_NAME = "build-a-dress-create-recovery";
const CLAIM_DATABASE_VERSION = 1;
const CLAIM_STORE_NAME = "pending-message-claims";

type StoredPendingMessageClaimStatus = "in_flight" | "completed";

interface StoredPendingMessageClaim {
  messageId?: string;
  claimedAt: number;
  updatedAt?: number;
  ownerId?: string;
  status?: StoredPendingMessageClaimStatus;
}

export type PendingMessageClaimOutcome =
  | "completed"
  | "action_failed"
  | "already_consumed"
  | "unavailable";

type PendingMessageClaimDecision =
  | { kind: "claimed" }
  | { kind: "already_consumed" }
  | { kind: "in_flight"; retryAfterMs: number }
  | { kind: "unavailable" };

type PendingMessageClaimAuthority = "indexed_db" | "local_storage";

interface PendingMessageClaimHandle {
  authority: PendingMessageClaimAuthority;
  messageId: string;
  ownerId: string;
}

interface PendingMessageClaimResult {
  decision: PendingMessageClaimDecision;
  handle?: PendingMessageClaimHandle;
}

function storageKey(messageId: string): string {
  return `${CLAIM_STORAGE_PREFIX}${messageId}`;
}

function createOwnerId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    // Fall through to a browser-local uniqueness fallback.
  }
  return `claim-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function claimTimestamp(claim: StoredPendingMessageClaim): number {
  const timestamp = Number(claim.updatedAt ?? claim.claimedAt);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function inspectClaim(
  claim: StoredPendingMessageClaim | undefined,
  now: number,
  reclaimInFlight: boolean
): PendingMessageClaimDecision {
  if (!claim) return { kind: "claimed" };

  const timestamp = claimTimestamp(claim);
  const ageMs = Math.max(0, now - timestamp);

  // Records created by the previous implementation did not include a status.
  // They represented a finished one-shot claim and remain a replay guard until
  // their original 24-hour retention window expires.
  if (!claim.status || claim.status === "completed") {
    return ageMs <= CLAIM_COMPLETED_MAX_AGE_MS
      ? { kind: "already_consumed" }
      : { kind: "claimed" };
  }

  if (reclaimInFlight || ageMs >= CLAIM_IN_FLIGHT_LEASE_MS) {
    return { kind: "claimed" };
  }

  return {
    kind: "in_flight",
    retryAfterMs: Math.max(1, CLAIM_IN_FLIGHT_LEASE_MS - ageMs),
  };
}

function readLocalClaim(
  messageId: string
): StoredPendingMessageClaim | undefined {
  const key = storageKey(messageId);
  const serialized = window.localStorage.getItem(key);
  if (!serialized) return undefined;

  try {
    return JSON.parse(serialized) as StoredPendingMessageClaim;
  } catch {
    window.localStorage.removeItem(key);
    return undefined;
  }
}

function writeLocalClaim(
  messageId: string,
  claim: StoredPendingMessageClaim
): boolean {
  const key = storageKey(messageId);
  const serialized = JSON.stringify(claim);
  window.localStorage.setItem(key, serialized);
  return window.localStorage.getItem(key) === serialized;
}

function localCompletedClaimDecision(
  messageId: string
): PendingMessageClaimDecision | null {
  const existing = readLocalClaim(messageId);
  if (!existing || existing.status === "in_flight") return null;

  const decision = inspectClaim(existing, Date.now(), false);
  if (decision.kind === "claimed") {
    window.localStorage.removeItem(storageKey(messageId));
    return null;
  }
  return decision;
}

function openClaimDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(CLAIM_DATABASE_NAME, CLAIM_DATABASE_VERSION);
    let settled = false;
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(CLAIM_STORE_NAME)) {
        database.createObjectStore(CLAIM_STORE_NAME, {
          keyPath: "messageId",
        });
      }
    };
    request.onsuccess = () => {
      if (settled) {
        request.result.close();
        return;
      }
      settled = true;
      resolve(request.result);
    };
    request.onerror = () => {
      if (settled) return;
      settled = true;
      reject(request.error ?? new Error("IndexedDB open failed"));
    };
    request.onblocked = () => {
      if (settled) return;
      settled = true;
      reject(new Error("IndexedDB open was blocked"));
    };
  });
}

/**
 * IndexedDB read-write transactions are serialized across tabs for one object
 * store. The get-and-put below is therefore an atomic compare-and-set even in
 * browsers that do not expose the Web Locks API.
 */
async function claimWithIndexedDb(
  factory: IDBFactory,
  messageId: string,
  ownerId: string,
  reclaimInFlight: boolean
): Promise<PendingMessageClaimDecision> {
  try {
    const localDecision = localCompletedClaimDecision(messageId);
    if (localDecision) return localDecision;
  } catch {
    // IndexedDB remains the authority when localStorage is disabled.
  }

  let database: IDBDatabase;
  try {
    database = await openClaimDatabase(factory);
  } catch {
    return { kind: "unavailable" };
  }

  return new Promise(resolve => {
    let decision: PendingMessageClaimDecision = { kind: "unavailable" };
    try {
      const transaction = database.transaction(CLAIM_STORE_NAME, "readwrite");
      const store = transaction.objectStore(CLAIM_STORE_NAME);
      const readRequest = store.get(messageId);

      readRequest.onsuccess = () => {
        const existing = readRequest.result as
          | StoredPendingMessageClaim
          | undefined;
        decision = inspectClaim(existing, Date.now(), reclaimInFlight);
        if (decision.kind !== "claimed") return;

        const now = Date.now();
        store.put({
          messageId,
          ownerId,
          status: "in_flight",
          claimedAt: now,
          updatedAt: now,
        } satisfies StoredPendingMessageClaim);
      };
      readRequest.onerror = () => {
        decision = { kind: "unavailable" };
        transaction.abort();
      };
      transaction.oncomplete = () => {
        database.close();
        resolve(decision);
      };
      transaction.onerror = () => {
        database.close();
        resolve({ kind: "unavailable" });
      };
      transaction.onabort = () => {
        database.close();
        resolve({ kind: "unavailable" });
      };
    } catch {
      database.close();
      resolve({ kind: "unavailable" });
    }
  });
}

async function updateIndexedDbClaim(
  factory: IDBFactory,
  handle: PendingMessageClaimHandle,
  status: StoredPendingMessageClaimStatus
): Promise<boolean> {
  let database: IDBDatabase;
  try {
    database = await openClaimDatabase(factory);
  } catch {
    return false;
  }

  return new Promise(resolve => {
    let updated = false;
    try {
      const transaction = database.transaction(CLAIM_STORE_NAME, "readwrite");
      const store = transaction.objectStore(CLAIM_STORE_NAME);
      const readRequest = store.get(handle.messageId);
      readRequest.onsuccess = () => {
        const existing = readRequest.result as
          | StoredPendingMessageClaim
          | undefined;
        if (
          !existing ||
          existing.status !== "in_flight" ||
          existing.ownerId !== handle.ownerId
        ) {
          return;
        }

        const now = Date.now();
        store.put({
          ...existing,
          messageId: handle.messageId,
          status,
          updatedAt: now,
        } satisfies StoredPendingMessageClaim);
        updated = true;
      };
      readRequest.onerror = () => transaction.abort();
      transaction.oncomplete = () => {
        database.close();
        resolve(updated);
      };
      transaction.onerror = () => {
        database.close();
        resolve(false);
      };
      transaction.onabort = () => {
        database.close();
        resolve(false);
      };
    } catch {
      database.close();
      resolve(false);
    }
  });
}

function claimWithLocalStorage(
  messageId: string,
  ownerId: string,
  reclaimInFlight: boolean
): PendingMessageClaimDecision {
  const existing = readLocalClaim(messageId);
  const decision = inspectClaim(existing, Date.now(), reclaimInFlight);
  if (decision.kind !== "claimed") return decision;

  const now = Date.now();
  return writeLocalClaim(messageId, {
    messageId,
    ownerId,
    status: "in_flight",
    claimedAt: now,
    updatedAt: now,
  })
    ? decision
    : { kind: "unavailable" };
}

function updateLocalStorageClaim(
  handle: PendingMessageClaimHandle,
  status: StoredPendingMessageClaimStatus
): boolean {
  const existing = readLocalClaim(handle.messageId);
  if (
    !existing ||
    existing.status !== "in_flight" ||
    existing.ownerId !== handle.ownerId
  ) {
    return false;
  }

  return writeLocalClaim(handle.messageId, {
    ...existing,
    messageId: handle.messageId,
    status,
    updatedAt: Date.now(),
  });
}

async function beginClaim(
  messageId: string,
  ownerId: string,
  reclaimInFlight: boolean,
  allowLocalStorageAuthority: boolean
): Promise<PendingMessageClaimResult> {
  // If IndexedDB is exposed, it is the only claim authority. Falling back to
  // localStorage after an open/transaction failure creates two independent
  // histories: an earlier IDB-only claim can then be replayed by an LS-only
  // invocation (or vice versa) as browser capabilities change.
  if (typeof indexedDB !== "undefined") {
    const decision = await claimWithIndexedDb(
      indexedDB,
      messageId,
      ownerId,
      reclaimInFlight
    );
    return decision.kind === "claimed"
      ? {
          decision,
          handle: { authority: "indexed_db", messageId, ownerId },
        }
      : { decision };
  }

  if (!allowLocalStorageAuthority) {
    return { decision: { kind: "unavailable" } };
  }

  try {
    const decision = claimWithLocalStorage(messageId, ownerId, reclaimInFlight);
    return decision.kind === "claimed"
      ? {
          decision,
          handle: { authority: "local_storage", messageId, ownerId },
        }
      : { decision };
  } catch {
    return { decision: { kind: "unavailable" } };
  }
}

async function updateClaim(
  handle: PendingMessageClaimHandle,
  status: StoredPendingMessageClaimStatus
): Promise<boolean> {
  if (handle.authority === "local_storage") {
    try {
      return updateLocalStorageClaim(handle, status);
    } catch {
      return false;
    }
  }

  if (typeof indexedDB === "undefined") return false;
  const updated = await updateIndexedDbClaim(indexedDB, handle, status);

  // Mirror the lifecycle for older builds that only know localStorage. This is
  // never used as a fallback authority while IndexedDB exists.
  try {
    const existing = readLocalClaim(handle.messageId);
    if (
      !existing ||
      existing.ownerId === handle.ownerId ||
      inspectClaim(existing, Date.now(), false).kind === "claimed"
    ) {
      const now = Date.now();
      writeLocalClaim(handle.messageId, {
        messageId: handle.messageId,
        ownerId: handle.ownerId,
        status,
        claimedAt: existing?.claimedAt ?? now,
        updatedAt: now,
      });
    }
  } catch {
    // IndexedDB remains the durable authority.
  }

  return updated;
}

function waitForClaim(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms));
}

async function claimUntilSettled(
  messageId: string,
  ownerId: string,
  reclaimInFlight: boolean,
  allowLocalStorageAuthority: boolean
): Promise<PendingMessageClaimResult> {
  while (true) {
    const result = await beginClaim(
      messageId,
      ownerId,
      reclaimInFlight,
      allowLocalStorageAuthority
    );
    if (result.decision.kind !== "in_flight") return result;

    await waitForClaim(
      Math.max(1, Math.min(result.decision.retryAfterMs, CLAIM_POLL_MAX_MS))
    );
  }
}

async function runClaimedAction(
  handle: PendingMessageClaimHandle,
  onResume: () => void | Promise<void>
): Promise<PendingMessageClaimOutcome> {
  // A live owner keeps extending the IDB lease. A second no-lock tab waits for
  // completion; if this tab disappears, heartbeats stop and the orphaned claim
  // becomes reclaimable instead of permanently swallowing the customer's turn.
  const heartbeat = window.setInterval(() => {
    void updateClaim(handle, "in_flight");
  }, CLAIM_HEARTBEAT_MS);

  let outcome: PendingMessageClaimOutcome = "completed";
  try {
    await onResume();
  } catch {
    outcome = "action_failed";
  } finally {
    window.clearInterval(heartbeat);
    // A callback that returned or threw reached a known terminal state. Keep
    // the automatic one-shot consumed in both cases; failures are restored as
    // a visible manual draft by the caller. Only an owner that disappears
    // before this finally block leaves an in-flight lease to recover.
    await updateClaim(handle, "completed");
  }
  return outcome;
}

/**
 * Claims a restored customer message once across every same-origin tab.
 *
 * Web Locks order contenders while IndexedDB (or, only when IDB truly does not
 * exist, localStorage) records an in-flight lease and the completed one-shot.
 * Browsers without Web Locks rely on IndexedDB's serialized transactions plus
 * a heartbeat, so concurrent tabs do not duplicate and an abandoned claim can
 * eventually be recovered.
 */
export async function resumePendingSubmittedMessageOnce(
  messageId: string,
  onResume: () => void | Promise<void>
): Promise<PendingMessageClaimOutcome> {
  if (
    typeof window === "undefined" ||
    typeof navigator === "undefined" ||
    !messageId.trim()
  ) {
    return "unavailable";
  }

  const ownerId = createOwnerId();

  if (!navigator.locks) {
    const result = await claimUntilSettled(messageId, ownerId, false, false);
    if (result.decision.kind !== "claimed" || !result.handle) {
      return result.decision.kind === "already_consumed"
        ? "already_consumed"
        : "unavailable";
    }
    return runClaimedAction(result.handle, onResume);
  }

  try {
    return await navigator.locks.request(
      `${CLAIM_STORAGE_PREFIX}${messageId}`,
      async (): Promise<PendingMessageClaimOutcome> => {
        // Acquiring the message's exclusive Web Lock proves that another
        // lock-aware owner is no longer running. Its leftover in-flight record
        // is therefore orphaned and can be reclaimed immediately.
        const result = await claimUntilSettled(messageId, ownerId, true, true);
        if (result.decision.kind !== "claimed" || !result.handle) {
          return result.decision.kind === "already_consumed"
            ? "already_consumed"
            : "unavailable";
        }
        return runClaimedAction(result.handle, onResume);
      }
    );
  } catch {
    return "unavailable";
  }
}
